import transactionImage from "../../assets/images/transactions/index";
import {
  BenefitCategories,
  FundsOrigin,
  MerchantData,
  PurchaseStatus,
  TransactionType,
} from "../../pages/transactions/useTransactions";

interface TransactionProps {
  name?: string;
  tag: string;
  amount: string;
  taxExemption: string;
  fundOrigins: FundsOrigin[];
  state: PurchaseStatus;
  type: TransactionType;
  merchant?: MerchantData;
  benefit?: BenefitCategories;
}

const TagColorsByFundOrigin = {
  [FundsOrigin.FROM_COMPANY]: "text-accent-dark",
  [FundsOrigin.FROM_BENEFIT_BAG]: "text-success-dark",
  [FundsOrigin.FROM_SALARY]: "text-info-dark",
  [FundsOrigin.FROM_ACCUMULATED_SALARY]: "text-gray-700",
  [FundsOrigin.FROM_ACCUMULATED_ALLOWANCE]: "text-gray-700",
  shared: "text-secondary-dark",
};

const hasLineThroughStatus = [
  PurchaseStatus.CANCELLED,
  PurchaseStatus.REJECTED,
];

const Transaction = ({
  name,
  tag,
  amount,
  taxExemption,
  fundOrigins,
  state,
  type,
  merchant,
  benefit,
}: TransactionProps) => {
  const getTransactionTagColor = () => {
    if (state === PurchaseStatus.CANCELLED) return "text-gray-700";
    const fundOriginItem = fundOrigins?.length > 1 ? "shared" : fundOrigins[0];
    return TagColorsByFundOrigin[fundOriginItem] || "text-accent-dark";
  };

  const getTransactionImage = () => {
    if ([PurchaseStatus.REJECTED, PurchaseStatus.CANCELLED].includes(state)) {
      return transactionImage[state];
    }
    if ([TransactionType.REFUND, TransactionType.WITHDRAWAL].includes(type))
      return transactionImage[type];
    if (merchant?.imageUrl) return transactionImage.unknown;
    if (merchant?.brand)
      return (
        transactionImage[merchant.brand] ??
        (benefit && transactionImage[benefit]) ??
        transactionImage.unknown
      );
    if (benefit) return transactionImage[benefit];
    return transactionImage.unknown;
  };

  const transactionColor = getTransactionTagColor();
  const hasLineThrough = hasLineThroughStatus.includes(state) && "line-through";

  const transactionImageSrc = getTransactionImage();

  return (
    <div className="flex items-start p-4 self-stretch">
      <div className="w-10 relative py-[3px]">
        <img src={transactionImageSrc} className="rounded-3xl" />
        {state === PurchaseStatus.PENDING && (
          <img
            src={transactionImage.pending}
            className="absolute bottom-0 left-[29px] w-4"
          />
        )}
      </div>
      <div className="flex flex-col items-start gap-1 flex-1 ml-2">
        <div className="flex justify-between items-center self-stretch">
          <div className="flex h-6 justify-center flex-col">
            <p className="text-neutrals-gray-900 font-medium text-sm">{name}</p>
          </div>
          <div className="flex flex-col justify-center w-24 h-6">
            <p
              className={`text-neutrals-gray-900 text-right font-medium text-base ${hasLineThrough}`}
            >
              {amount}
            </p>
          </div>
        </div>
        <div className="flex justify-between items-center self-stretch">
          <div className="flex flex-col items-start gap-y-2">
            <p className={`font-medium text-xs ${transactionColor}`}>{tag}</p>
          </div>
          <div>
            <p className="text-right text-gray-500 text-xs">{taxExemption}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transaction;
